// API client for customers
import { BASE } from './../../config/setting';

function getAuthHeader() {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function handleResponse(res) {
  if (!res.ok) {
    const contentType = res.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const error = await res.json();
      throw new Error(error.message || JSON.stringify(error));
    } else {
      throw new Error(
        `API Error (${res.status}): ${res.statusText}. ` +
        `Make sure backend is running at ${BASE}`
      );
    }
  }
  return res.json();
}

/**
 * Fetch customers with pagination and filters
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 * @param {Object} filters - { status, search }
 */
export async function fetchCustomers(page = 1, limit = 20, filters = {}) {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (filters.status) {
      params.append('status', filters.status);
    }
    if (filters.search) {
      params.append('search', filters.search);
    }
    
    const res = await fetch(`${BASE}/api/customers?${params}`, {
      headers: { ...getAuthHeader() },
    });
    return await handleResponse(res);
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error(`Cannot connect to backend at ${BASE}. Make sure it's running.`);
    }
    throw error;
  }
}

/**
 * Fetch single customer by ID
 */
export async function fetchCustomer(id) {
  try {
    const res = await fetch(`${BASE}/api/customers/${id}`, {
      headers: { ...getAuthHeader() },
    });
    return await handleResponse(res);
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error(`Cannot connect to backend at ${BASE}. Make sure it's running.`);
    }
    throw error;
  }
}

/**
 * Create a new customer
 */
export async function createCustomer(payload) {
  try {
    const res = await fetch(`${BASE}/api/customers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    return await handleResponse(res);
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error(`Cannot connect to backend at ${BASE}. Make sure it's running.`);
    }
    throw error;
  }
}

/**
 * Update customer by ID
 */
export async function updateCustomer(id, payload) {
  try {
    const res = await fetch(`${BASE}/api/customers/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    return await handleResponse(res);
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error(`Cannot connect to backend at ${BASE}. Make sure it's running.`);
    }
    throw error;
  }
}

/**
 * Delete customer by ID
 */
export async function deleteCustomer(id) {
  try {
    const res = await fetch(`${BASE}/api/customers/${id}`, {
      method: 'DELETE',
      headers: { ...getAuthHeader() },
    });
    return await handleResponse(res);
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error(`Cannot connect to backend at ${BASE}. Make sure it's running.`);
    }
    throw error;
  }
}

/**
 * Fetch dashboard statistics
 */
export async function fetchDashboardStats() {
  try {
    const res = await fetch(`${BASE}/api/customers/dashboard-stats`, {
      headers: { ...getAuthHeader() },
    });
    return await handleResponse(res);
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error(`Cannot connect to backend at ${BASE}. Make sure it's running.`);
    }
    throw error;
  }
}

/**
 * Fetch migration categories
 */
export async function fetchMigrationCategories() {
  try {
    const res = await fetch(`${BASE}/api/customers/migration-categories`, {
      headers: { ...getAuthHeader() },
    });
    return await handleResponse(res);
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error(`Cannot connect to backend at ${BASE}. Make sure it's running.`);
    }
    throw error;
  }
}
